/**
 * convertHalfWidthNumbersAndHyphensToFullWidth
 * @description 半角の数字とハイフンを全角文字に変換する（電話番号で使用）
 * @param telephoneNumberWithHyphen
 * @return string
 */
export const convertHalfWidthNumbersAndHyphensToFullWidth = (
  telephoneNumberWithHyphen: string
): string => {
  return telephoneNumberWithHyphen.replace(/[0-9/-]/g, function (s) {
    return String.fromCharCode(s.charCodeAt(0) + 0xfee0);
  });
};

/**
 * formatConcatenatedString
 * @description 複数の文字列を指定されたセパレータで結合する
 * @param parts 結合する文字列の配列
 * @param separator 結合に使用するセパレータ（デフォルトはスペース）
 * @return string 結合された文字列
 */
export const formatConcatenatedString = (parts: string[], separator: string = ' '): string => {
  return parts.filter((part) => part).join(separator);
};

/**
 * formatPostalCode
 * @description 郵便番号を[3桁]-[4桁]の形式にフォーマットする
 * @param firstPart 3桁の郵便番号文字列
 * @param secondPart 4桁の郵便番号文字列
 * @return string フォーマットされた郵便番号
 */
export const formatPostalCode = (firstPart: string, secondPart: string): string => {
  return formatConcatenatedString([firstPart, secondPart], '-');
};

/**
 * formatAddress
 * @description 住所の構成要素を受け取り、nullや空文字ではない部分をスペースで結合してフォーマットされた住所を返す
 * @param addressParts 住所の構成要素を含むオブジェクト（prefecture, city, town, town2, building）
 * @return string フォーマットされた住所
 */
export const formatAddress = (addressParts: {
  prefecture: string;
  city: string;
  town: string | null;
  town2: string | null;
  building: string | null;
}): string => {
  return [
    addressParts.prefecture,
    addressParts.city,
    addressParts.town,
    addressParts.town2,
    addressParts.building,
  ]
    .filter((part) => part !== null && part !== '')
    .join(' ');
};

/**
 * formatPhoneNumber
 * @description 電話番号の構成要素を受け取り、ハイフンで結合してフォーマットされた電話番号を返す
 * @param telParts 電話番号の構成要素を含むオブジェクト（tel1, tel2, tel3）
 * @return string フォーマットされた電話番号
 */
export const formatPhoneNumber = (telParts: {
  tel1: string;
  tel2: string;
  tel3: string;
}): string => {
  return formatConcatenatedString([telParts.tel1, telParts.tel2, telParts.tel3], '-');
};

/**
 * assembleDetailPagePath
 * @description 商品コードを受け取り、商品の詳細ページのパスを生成する
 * @param productCode 商品コード
 * @return string 商品の詳細ページのパス
 */
export const assembleDetailPagePath = (productCode: string): string => {
  return `/products/${productCode}`;
};

/**
 * normalizeCardHolderName
 * @description カード名義人の入力値を半角英字・スペースに変換し、全て大文字に統一する
 * 全角英字→半角英字、全角スペース→半角スペース、連続スペース→1つにまとめる
 * @param holderName カード名義人の入力値
 * @return string 変換後のカード名義人
 */
export const normalizeCardHolderName = (holderName: string): string => {
  return holderName
    .replace(/[Ａ-Ｚａ-ｚ]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xfee0))
    .toUpperCase()
    .replace(/　/g, ' ')
    .replace(/\s+/g, ' ');
};

/**
 * splitHonorific
 * @description 氏名を「本体」と末尾の敬称に分ける。支給デザインは氏名を
 * `オール阪神<span>さん</span>` の形で出し、CSS が `<span>` だけ小さくする
 * （`style.css:8174` / `:8625`）ため、敬称だけを別要素に出せるようにする。
 * 末尾が敬称でなければ敬称は空文字を返す。
 * @param name 氏名（例: `オール阪神さん`）
 * @param honorific 末尾の敬称（既定は `さん`）
 * @return { body: string; honorific: string }
 */
export const splitHonorific = (
  name: string,
  honorific: string = 'さん'
): { body: string; honorific: string } => {
  return name.endsWith(honorific)
    ? { body: name.slice(0, -honorific.length), honorific }
    : { body: name, honorific: '' };
};
